// Reflect: a quiet reflection, one prompt at a time, any skipped. It starts from the day's quote (the
// iOS daily card's second page, on the same rotation), or today's verse, or today's reading in a plan,
// or a section of a book. Reflections are private notes, synced to the reader's own devices (tagged
// "reflection"; studyQueries.ts) and never shown to anyone else: a list
// by date below, and an export as a text file. One line can still be shared with friends, by choice.
//
// Set as a calm room (design/DIRECTION.md §11): La Tour's candle, the passage on glass over it, and
// the questions turning one at a time beside it, as onboarding's do (Steps). What is kept is a plate.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Art } from "../../components/Art";
import { Button } from "../../components/Button";
import { Icon } from "../../components/Icon";
import { Plate } from "../../components/Surface";
import { SegmentedControl } from "../../components/Tabs";
import ui from "../../components/ui.module.css";
import { dailyIndex } from "../../data/library";
import { useJourneys } from "../../data/planQueries";
import { localDate, type Reading } from "../../data/plans";
import { lookupPassage } from "../../data/passages";
import { useCreatePost, useVotd } from "../../data/queries";
import { REFLECT_PROMPTS, reflectionsText, type Reflection, type ReflectionSource } from "../../data/study";
import { useReflections, useSaveReflection } from "../../data/studyQueries";
import { blocksText, findSection, pageOf, usePage, useQuotes, useToc, useWork } from "../../data/works";
import { useDraft } from "../../lib/useDraft";
import { useTitle } from "../../lib/useTitle";
import { Steps } from "../../motion/Steps";
import s from "./reflect.module.css";

type Search = { ref?: string; book?: string; section?: string; from?: "quote" | "verse" | "plan" };
type From = NonNullable<Search["from"]>;

export const Route = createFileRoute("/_app/reflect")({
  validateSearch: (q: Record<string, unknown>): Search => {
    const out: Search = {};
    if (typeof q.ref === "string" && q.ref.length < 40) out.ref = q.ref;
    if (typeof q.book === "string" && /^[a-z0-9-]{1,80}$/.test(q.book) && typeof q.section === "string" && q.section.length < 300) {
      out.book = q.book;
      out.section = q.section;
    }
    if (q.from === "quote" || q.from === "verse" || q.from === "plan") out.from = q.from;
    return out;
  },
  component: Reflect,
});

/** What is being reflected on, ready to show and to keep. */
type Subject = { key: string; label: string; text: string | null; caption: string; source: ReflectionSource; link?: { id: string; section: string } | { ref: string } };

/** Today on the reader's own calendar, for the page's date. */
const todayIso = () => localDate();
const long = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

function Reflect() {
  useTitle("Reflect");
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data: votd } = useVotd();
  const { data: quotes } = useQuotes();
  const { data: journeys = [] } = useJourneys();

  // Today's plan reading: the first plan with a day appointed today.
  const planReading: Reading | undefined = journeys.find((j) => !j.me.paused && j.me.today?.readings.length)?.me.today?.readings[0];
  const from = search.ref || search.book ? null : (search.from ?? "quote");
  const quote = quotes?.length ? quotes[dailyIndex() % quotes.length]! : null;

  const fromBook = search.book
    ? { book: search.book, section: search.section! }
    : from === "plan" && planReading?.kind === "section"
      ? { book: planReading.libraryId, section: planReading.sectionUid }
      : null;
  const ref = search.ref ?? (from === "plan" && planReading?.kind === "scripture" ? planReading.label : null);
  const section = useSectionSubject(fromBook);

  let subject: Subject | null = null;
  if (fromBook) subject = section;
  else if (ref) {
    const p = lookupPassage(ref);
    if (p) subject = { key: p.key, label: p.label, text: p.text, caption: `${p.label} · ${p.translation}`, source: { kind: "scripture" }, link: { ref: p.label } };
  } else if (from === "verse" && votd)
    subject = { key: votd.ref.key, label: votd.ref.label, text: votd.quote, caption: `${votd.ref.label} · ${votd.translation}`, source: { kind: "scripture" }, link: { ref: votd.ref.label } };
  else if (from === "quote" && quote)
    subject = {
      key: `q:${quote.libraryId}`,
      label: `${quote.author}, ${quote.work}`,
      text: quote.text,
      caption: `${quote.author}, ${quote.work}`,
      source: { kind: "quote", text: quote.text, author: quote.author, work: quote.work, libraryId: quote.libraryId, sectionUid: quote.sectionUid },
      ...(quote.sectionUid ? { link: { id: quote.libraryId, section: quote.sectionUid } } : {}),
    };

  const options: { value: From; label: string }[] = [
    { value: "quote", label: "Quote" },
    { value: "verse", label: "Verse" },
    ...(planReading ? [{ value: "plan" as const, label: "Plan reading" }] : []),
  ];

  return (
    <div className={s.page}>
      <header className={s.head}>
        <div>
          <h1 className={ui.title}>Reflect</h1>
          <p className={s.lead}>
            <Icon name="lock" size={16} />
            Kept for you alone, on every device you sign in to.
          </p>
        </div>
        {from && <SegmentedControl label="Reflect on today’s" options={options} value={from} onChange={(v) => void navigate({ to: "/reflect", search: { from: v }, replace: true })} />}
      </header>

      {subject ? <Session key={subject.key} subject={subject} /> : <div className={s.loading} aria-busy="true" aria-label="Opening today’s reading" />}

      <Past />
    </div>
  );
}

/** A section of a book as the thing reflected on: its title, its work and (when the web may show it) its opening lines. */
function useSectionSubject(at: { book: string; section: string } | null): Subject | null {
  const { data: work } = useWork(at?.book);
  const { data: toc } = useToc(work ? at?.book : null);
  const sec = toc && at ? findSection(toc, at.section) : undefined;
  const n = toc && at ? pageOf(toc, at.section) : -1;
  const { data: page } = usePage(at?.book ?? "", n, !!work?.text && !!sec);
  return useMemo(() => {
    if (!at || !work || !sec) return null;
    const part = page?.parts.find((p) => p.uid === at.section) ?? page?.parts[0];
    const text = part ? blocksText(part.blocks.filter((b) => b.t !== "h")) : null;
    const excerpt = text && text.length > 700 ? `${text.slice(0, 700).replace(/\s+\S*$/, "")} …` : text;
    return {
      key: `b:${work.id}/${sec.uid}`,
      label: `${sec.label}, ${work.title}`,
      text: work.text ? (excerpt ?? null) : null,
      caption: `${sec.label} · ${work.shortTitle}`,
      source: { kind: "section", libraryId: work.id, sectionUid: sec.uid, workTitle: work.title },
      ...(work.text ? { link: { id: work.id, section: sec.uid } } : {}),
    };
  }, [at, work, sec, page]);
}

function Session({ subject }: { subject: Subject }) {
  const src = subject.source;
  const prompts = useMemo(
    () =>
      REFLECT_PROMPTS.map((p, i) =>
        i === 0 && src.kind === "quote" ? { ...p, prompt: `What is ${/Assembly|Churches|Various/.test(src.author) ? "it" : src.author} saying?`, hint: "In your own words. Then: does it ring true?" } : p,
      ),
    [src],
  );
  const [answers, setAnswers, clearAnswers] = useDraft<string[]>(`reflect:${subject.key}`, ["", "", "", ""]);
  const [{ step, direction }, setAt] = useState({ step: 0, direction: 1 });
  const [saved, setSaved] = useState<{ prompt: string; text: string }[] | null>(null);
  const save = useSaveReflection();
  const create = useCreatePost();
  const navigate = useNavigate();
  const p = prompts[step];
  const last = step === prompts.length - 1;
  const go = (to: number) => setAt({ step: to, direction: to >= step ? 1 : -1 });

  const finish = () => {
    const filled = prompts.map((x, i) => ({ prompt: x.prompt, text: answers[i]!.trim() })).filter((a) => a.text);
    if (filled.length === 0) return go(0);
    save.mutate({ refKey: subject.key, refLabel: subject.label, source: src, answers: filled }, { onSuccess: () => (setSaved(filled), clearAnswers()) });
  };

  const canShare = src.kind === "scripture" || (src.kind === "quote" && !!src.sectionUid);
  const shareLine = (text: string) => {
    const goTo = { onSuccess: (post: { id: string }) => void navigate({ to: "/p/$postId", params: { postId: post.id } }) };
    if (src.kind === "scripture") create.mutate({ kind: "verse", refKey: subject.label, translation: "WEB", body: text, visibility: "friends" }, goTo);
    else if (src.kind === "quote" && src.sectionUid)
      create.mutate(
        {
          kind: "highlight",
          work: { id: src.libraryId, label: `${src.author} · ${src.work}`.slice(0, 200), locator: "" },
          source: { libraryId: src.libraryId, sectionUid: src.sectionUid },
          quote: src.text,
          body: text,
          visibility: "friends",
        },
        goTo,
      );
  };

  return (
    <div className={s.stage}>
      <section className={s.room} aria-label="What you are reflecting on">
        <Art name="latour" decorative priority arrive focus="50% 40%" className={s.art} />
        <figure className={s.passage}>
          <figcaption className={s.ref}>{subject.caption}</figcaption>
          {subject.text ? (
            <blockquote data-long={subject.text.length > 320 || undefined}>{subject.text}</blockquote>
          ) : (
            <p className={s.away}>Read this section in the Bible Standard app, then come back to reflect on it.</p>
          )}
          {subject.link && (
            <p className={s.context}>
              {"ref" in subject.link ? (
                <Link to="/r/$ref" params={{ ref: subject.link.ref }}>
                  Read the passage
                </Link>
              ) : (
                <Link to="/books/$id/$section" params={{ id: subject.link.id, section: subject.link.section }}>
                  {src.kind === "quote" ? "Read it in context" : "Read the whole section"}
                </Link>
              )}
              <Icon name="forward" size={14} />
            </p>
          )}
        </figure>
      </section>

      {/* The notebook's binding, between its two pages on a wide screen (FW-006). */}
      <span className={s.rings} aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
        <i />
      </span>

      <div className={s.flow}>
        {saved ? (
          <Plate pad="lg" className={s.done} aria-live="polite">
            <span className={s.ornament} aria-hidden="true">
              <i />
            </span>
            <p className={s.doneTitle}>Kept, for you alone.</p>
            {canShare && <p className={s.doneHint}>If one line would help a friend, you can share just that line with the passage.</p>}
            <ul className={s.lines}>
              {saved.map((a) => (
                <li key={a.prompt}>
                  <p className={s.linePrompt}>{a.prompt}</p>
                  <p className={s.lineText}>{a.text}</p>
                  {canShare && (
                    <Button variant="text" size="sm" icon="share" onClick={() => shareLine(a.text)} disabled={create.isPending}>
                      Share this line with friends
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </Plate>
        ) : (
          p && (
            <form
              className={s.prompt}
              onSubmit={(e) => {
                e.preventDefault();
                if (!last) go(step + 1);
                else finish();
              }}
            >
              <div className={s.pageTop}>
                <div className={s.count} role="img" aria-label={`Question ${step + 1} of ${prompts.length}`}>
                  {prompts.map((_, i) => (
                    <span key={i} className={i === step ? s.on : i < step ? s.past : undefined} />
                  ))}
                </div>
                <time className={s.today} dateTime={todayIso()}>
                  {long(todayIso())}
                </time>
              </div>
              <Steps step={step} direction={direction} className={s.step}>
                <label htmlFor="answer" className={s.question}>
                  {p.prompt}
                </label>
                <p className={s.hint} id="answer-hint">
                  {p.hint}
                </p>
                <textarea
                  id="answer"
                  className={`${ui.area} ${s.answer}`}
                  rows={6}
                  aria-describedby="answer-hint"
                  value={answers[step]}
                  onChange={(e) => setAnswers(answers.map((a, i) => (i === step ? e.target.value : a)))}
                />
              </Steps>
              <div className={s.actions}>
                {step > 0 && (
                  <Button variant="ghost" icon="back" onClick={() => go(step - 1)}>
                    Back
                  </Button>
                )}
                <span className={s.spacer} />
                {!last && (
                  <Button variant="text" onClick={() => go(step + 1)}>
                    Skip
                  </Button>
                )}
                <Button type="submit" variant="primary" busy={save.isPending}>
                  {last ? "Keep this" : "Next"}
                </Button>
              </div>
            </form>
          )
        )}
      </div>
    </div>
  );
}

/** Every reflection kept, by date, and a way to take them away as a text file. */
function Past() {
  const { data: past = [] } = useReflections();
  const [open, setOpen] = useState<string | null>(null);
  const byDate = useMemo(() => {
    const m = new Map<string, Reflection[]>();
    for (const r of past) m.set(r.date, [...(m.get(r.date) ?? []), r]);
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [past]);
  if (!past.length) return null;

  const download = () => {
    const blob = new Blob([reflectionsText(past)], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `reflections-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <section className={s.pastSection} aria-labelledby="past-head">
      <div className={s.pastHead}>
        <h2 id="past-head" className={s.pastTitle}>
          Your reflections
        </h2>
        <Button variant="ghost" size="sm" icon="download" onClick={download}>
          Export as text
        </Button>
      </div>
      {byDate.map(([date, items]) => (
        <div key={date} className={s.day}>
          <h3 className={s.entryDate}>{long(date)}</h3>
          {items.map((r) => (
            <article key={r.id} className={s.entry}>
              <button type="button" className={s.entryHead} aria-expanded={open === r.id} onClick={() => setOpen(open === r.id ? null : r.id)}>
                <span className={s.entryLabel}>{r.refLabel}</span>
                <span className={s.entryFirst}>{r.answers[0]?.text}</span>
                <span className={s.entryCaret} aria-hidden="true">
                  <Icon name="caret" size={14} />
                </span>
              </button>
              {open === r.id && (
                <div className={s.entryBody}>
                  {r.source?.kind === "quote" && <p className={s.entryQuote}>“{r.source.text}”</p>}
                  {r.answers.map((a) => (
                    <div key={a.prompt}>
                      <p className={s.linePrompt}>{a.prompt}</p>
                      <p className={s.lineText}>{a.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      ))}
    </section>
  );
}
